import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { useHistory } from 'react-router';

import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import { useRequest } from '@common/services/api';
import { OverviewSection } from '@common/components/overview';
import { FormIcon } from '@modules/forms/components/form-icon';
import { createEvaluation } from '@modules/performance/action/create-evaluation';
import { useDidComponentMount } from '@common/hooks';
import Spinner from '@common/components/spinner';
import SearchBar from '@common/components/search-bar';
import { FormsMeta } from '@modules/performance/types';
import Placeholder from '@common/components/placeholder';
import { RadioButton } from '@common/components/radio-button/radio-button';
import { Toggle } from '@common/components/form/inputs/toggle';
import { DateInputHookForm } from '@common/components/form';
import { Button } from '@common/components/button';
import type { ExtendedUser, LoggedUser } from '@common/types/objects';

type EvaluationModalProps = {
  show: boolean;
  onHide: () => void;
  user: ExtendedUser | LoggedUser;
  organisationId: string;
  loggedUser: LoggedUser;
  basePath: string;
};

export const EvaluationModal = ({
  show,
  onHide,
  user,
  organisationId,
  loggedUser,
  basePath,
}: EvaluationModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [hasDeadline, setHasDeadline] = useState(false);
  const [isCreatingEvaluation, setIsCreatingEvaluation] = useState(false);
  const [searchForm, setSearchForm] = useState<string | null>(null);
  const didMount = useDidComponentMount();

  const { control } = useForm<{ deadline?: string }>({
    defaultValues: {},
  });

  const deadline = useWatch({
    control,
    name: 'deadline',
  });

  const formPayload = useMemo(
    () => ({
      ...(searchForm && searchForm !== '' ? { q: searchForm } : {}),
    }),
    [searchForm]
  );

  const {
    data: evaluationFormsData,
    meta: metaDataForms,
    loading: isLoadingEvaluationForms,
    refetch: refetchEvaluationForms,
    reset: resetEvaluationForms,
  } = useRequest(`/v1/organisations/${organisationId}/performance/forms`, {
    useCursor: true,
    payload: formPayload,
    enabled: show, // Only fetch when dialog is open
  });

  useEffect(() => {
    if (!didMount || !show) return;
    resetEvaluationForms();
  }, [formPayload, didMount, resetEvaluationForms, show]);

  const metaForms: FormsMeta = metaDataForms;

  const handleCreateEvaluation = useCallback(
    async (
      formId: string,
      reviewerIds: string[],
      userId: string,
      orgId: string,
      deadlineAt?: string | null
    ) => {
      try {
        setIsCreatingEvaluation(true);
        const deadlineDate = deadlineAt
          ? moment(deadlineAt).format('YYYY-MM-DD')
          : null;
        const data = await createEvaluation(orgId, userId, formId, {
          reviewerIds,
          deadlineAt: deadlineDate,
        });
        history.push(`${basePath}/performance/evaluations/${data.id}`);
        onHide();
        return data;
      } catch (error) {
        console.error('Error creating evaluation:', error);
        setIsCreatingEvaluation(false);
        throw error;
      }
    },
    [basePath, history, onHide]
  );

  const handleHide = useCallback(() => {
    setSelectedForm(null);
    setHasDeadline(false);
    setIsCreatingEvaluation(false);
    setSearchForm(null);
    onHide();
  }, [onHide]);

  const showsearch = useMemo(() => {
    const showSearch =
      metaForms?.pagination?.total_count > 10 ||
      !!searchForm ||
      !!metaForms?.pagination?.next_cursor ||
      evaluationFormsData?.length > 10 ||
      isLoadingEvaluationForms;
    return showSearch;
  }, [
    metaForms?.pagination?.total_count,
    searchForm,
    metaForms?.pagination?.next_cursor,
    evaluationFormsData?.length,
    isLoadingEvaluationForms,
  ]);

  if (!show) return null;

  return (
    <DialogWrapper
      show={show}
      size="small"
      onHide={handleHide}
      className="EditableModule"
      title={t('performance:select_evaluation')}
      onConfirm={() => {
        handleCreateEvaluation(
          selectedForm.id,
          [loggedUser.id],
          user.id,
          organisationId,
          hasDeadline ? deadline : null
        );
      }}
      confirmButtonText={t('performance:start_evaluation')}
      confirmButtonDisabled={!selectedForm || isCreatingEvaluation}
      confirmButtonLoading={isCreatingEvaluation}
      confirmButtonType="submit"
      confirmButtonForm="evaluationForm"
      content={
        <>
          {showsearch && (
            <SearchBar
              placeholder={t('common:search_list_search_placeholder')}
              defaultValue={searchForm || ''}
              filter={false}
              onSearch={setSearchForm}
            />
          )}
          <div className="tw-flex tw-flex-col tw-gap-3">
            {evaluationFormsData?.map(
              (row: any) => row.status === 'live' && (
              <div key={row.id} className="tw-flex tw-flex-col tw-gap-3">
                <OverviewSection
                  className="tw-flex tw-flex-col tw-gap-3
                        tw-p-2 tw-rounded-md tw-cursor-pointer tw-justify-between"
                  onClick={() => setSelectedForm(row)}
                >
                  <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-w-full">
                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
                      <FormIcon form={row} />
                      <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base tw-font-inter">
                        {row.title}
                      </div>
                    </div>
                    <RadioButton
                      value={row.id}
                      currentValue={selectedForm?.id || ''}
                      onChange={() => setSelectedForm(row)}
                    />
                  </div>
                  {selectedForm?.id === row.id && (
                  <div className="tw-border-l-2 tw-border-gray-200 tw-py-2">
                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                      <Toggle
                        value={hasDeadline}
                        onChange={(value) => setHasDeadline(value)}
                        label={t('performance:add_a_deadline')}
                      />
                    </div>
                    {hasDeadline && (
                    <div className="tw-flex tw-flex-col tw-bg-gray-custom-50 tw-rounded-lg tw-p-4 tw-gap-1 tw-mt-2">
                      <div className="tw-text-sm-plus tw-font-normal tw-text-gray-text">
                        {t('performance:deadline_date')}
                      </div>
                      <DateInputHookForm
                        control={control}
                        name="deadline"
                        placeholder={t('performance:select_a_date')}
                        minDate={moment().add(1, 'day').startOf('day')}
                      />
                    </div>
                          )}
                  </div>
                      )}
                </OverviewSection>
              </div>
                )
            )}
          </div>
          {!isLoadingEvaluationForms && evaluationFormsData?.length === 0 && (
            <Placeholder title={t('performance:no_evaluation_forms')} />
          )}
          {isLoadingEvaluationForms && <Spinner centered />}
          {!isLoadingEvaluationForms && !!metaForms?.pagination?.next_cursor && (
            <Button
              className="tw-w-full tw-mt-2"
              type="inverted"
              onClick={() => refetchEvaluationForms()}
            >
              {t('common:paginated_list_show_more')}
            </Button>
          )}
        </>
      }
    />
  );
};
